// Date utility functions to replace date-fns

/**
 * Get the start of the week (Monday)
 * @param {Date} date - The date to get the week start for
 * @returns {Date} - Start of week (Monday)
 */
export function startOfWeek(date, options = {}) {
  const weekStartsOn = options.weekStartsOn || 0; // 0 = Sunday, 1 = Monday
  const d = new Date(date);
  const day = d.getDay();
  const diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Get the end of the week (Sunday)
 * @param {Date} date - The date to get the week end for
 * @returns {Date} - End of week (Sunday)
 */
export function endOfWeek(date, options = {}) {
  const start = startOfWeek(date, options);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
}

/**
 * Add weeks to a date
 * @param {Date} date - The date to add weeks to
 * @param {number} amount - Number of weeks to add (can be negative)
 * @returns {Date} - New date with weeks added
 */
export function addWeeks(date, amount) {
  const result = new Date(date);
  result.setDate(result.getDate() + amount * 7);
  return result;
}

/**
 * Get all days in an interval
 * @param {Object} interval - { start: Date, end: Date }
 * @returns {Date[]} - Array of dates
 */
export function eachDayOfInterval(interval) {
  const { start, end } = interval;
  const days = [];
  const current = new Date(start);
  
  while (current <= end) {
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  
  return days;
}

/**
 * Format a date
 * @param {Date} date - The date to format
 * @param {string} formatStr - Format string (simplified version)
 * @returns {string} - Formatted date string
 */
export function format(date, formatStr) {
  const d = new Date(date);
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  const pad = (n) => n.toString().padStart(2, '0');
  
  switch (formatStr) {
    case 'EEE dd':
      return `${days[d.getDay()]} ${pad(d.getDate())}`;
    case 'EEE':
      return days[d.getDay()];
    case 'MMMM':
      return months[d.getMonth()];
    case 'd':
      return d.getDate().toString();
    case 'yyyy':
      return d.getFullYear().toString();
    case 'yyyy-MM-dd':
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    case 'h:mm a':
      const hours = d.getHours();
      const minutes = d.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      return `${displayHours}:${pad(minutes)} ${ampm}`;
    case 'MMMM d, yyyy':
      return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
    default:
      return d.toISOString();
  }
}

/**
 * Parse an ISO date string
 * @param {string} dateString - ISO date string
 * @returns {Date} - Parsed date
 */
export function parseISO(dateString) {
  return new Date(dateString);
}

/**
 * Check if two dates are the same day
 * @param {Date} date1 - First date
 * @param {Date} date2 - Second date
 * @returns {boolean} - True if same day
 */
export function isSameDay(date1, date2) {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

/**
 * Get start of today
 * @returns {Date} - Start of today
 */
export function startOfToday() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}


/**
 * Check if a date is today
 * @param {Date} date - The date to check
 * @returns {boolean} - True if the date is today
 */
export function isToday(date) {
  const today = new Date();
  const d = new Date(date);
  return (
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate()
  );
}
